import React, { useCallback, useEffect, useState } from 'react';
import type { AgentApproval } from '../../types/agent';
import { agentApi, apiErrorMessage } from '../../services/agentApi';
import { EmptyState, Spinner } from './agentUi';
import ApprovalCard from './ApprovalCard';
import { useAuth } from '../../contexts/AuthContext';

const ApprovalList: React.FC<{ onOpenRun: (runId: string) => void; onChanged: () => void }> = ({ onOpenRun, onChanged }) => {
  const { user } = useAuth();
  const [view, setView] = useState<'pending' | 'history'>('pending');
  const [items, setItems] = useState<AgentApproval[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await agentApi.approvals(view === 'pending' ? 'pending' : 'approved,rejected,expired');
      setItems(r.approvals);
      setError(null);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load approvals'));
    }
  }, [view]);

  useEffect(() => {
    load();
  }, [load]);

  const canDecide = (a: AgentApproval) => a.run?.userId === user?.id || user?.role === 'admin' || user?.role === 'system_admin';

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {(['pending', 'history'] as const).map((v) => (
          <button key={v} onClick={() => setView(v)} className={`text-xs px-2.5 py-1 rounded-full border ${view === v ? 'border-primary text-primary bg-primary/5' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'}`}>
            {v === 'pending' ? 'Waiting' : 'History'}
          </button>
        ))}
      </div>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}
      {items === null ? (
        <div className="flex items-center gap-2 text-gray-500 py-10 justify-center"><Spinner /> Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState title={view === 'pending' ? 'Nothing waiting for approval' : 'No decisions yet'}>
          {view === 'pending' ? 'When Arie needs your OK (a new customer, a master-data change, an external email, or shadow-mode proposals) it shows up here.' : null}
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id}>
              <ApprovalCard approval={a} canDecide={view === 'pending' && canDecide(a)} showRun onResolved={() => { load(); onChanged(); }} />
              <button className="text-xs text-primary hover:underline mt-1 ml-1" onClick={() => onOpenRun(a.runId)}>Open run →</button>
              {a.executionResult && view === 'history' && a.executionResult.error && <span className="text-xs text-red-600 dark:text-red-400 ml-2">{String(a.executionResult.error)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalList;
